import React, { useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import RecipeParamsForm, { paramsFromRecipe, paramsFromDefaults, paramsToPayload } from "@/components/RecipeParamsForm";
import ImageUploader from "@/components/ImageUploader";

const joinList = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const splitList = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export default function EditRecipe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, isRTL } = useI18n();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";
  const { user, loading: authLoading } = useAuth();
  const isAdmin = useAdmin();

  const { data: methods = [] } = useQuery({ queryKey: ["methods"], queryFn: endpoints.methods });
  const { data: recipe, isLoading } = useQuery({
    queryKey: ["recipe", id],
    queryFn: () => endpoints.getRecipe(id),
    enabled: !!id,
  });

  if (authLoading || isLoading || !recipe) {
    return <div className="max-w-2xl mx-auto p-10 text-[#5C544F]">{t.common.loading}</div>;
  }

  if (!user) return <Navigate to={`/login?next=/recipes/${id}/edit`} replace />;

  const canEdit = recipe && user && (isAdmin || recipe.user_id === user.user_id);

  if (!canEdit) {
    return (
      <div className="max-w-2xl mx-auto p-10 text-center" data-testid="edit-not-allowed">
        <p className="text-[#5C544F] mb-6">{isRTL ? "غير مصرّح لك بتعديل هذه الوصفة." : "You are not allowed to edit this recipe."}</p>
        <Button onClick={() => navigate("/recipes")} className="rounded-full bg-[#2A2421] text-[#F9F6F0]">
          {t.common.back}
        </Button>
      </div>
    );
  }

  return <EditForm recipe={recipe} methods={methods} navigate={navigate} t={t} lang={lang} isRTL={isRTL} serifClass={serifClass} isAdmin={isAdmin} />;
}

function EditForm({ recipe, methods, navigate, t, lang, isRTL, serifClass, isAdmin }) {
  const [form, setForm] = useState(() => ({
    title_en: recipe.title_en || "",
    title_ar: recipe.title_ar || "",
    method: recipe.method || "v60",
    difficulty: recipe.difficulty || "easy",
    image_url: recipe.image_url || "",
    ingredients_en: joinList(recipe.ingredients_en),
    ingredients_ar: joinList(recipe.ingredients_ar),
    steps_en: joinList(recipe.steps_en),
    steps_ar: joinList(recipe.steps_ar),
  }));
  const [params, setParams] = useState(() => paramsFromRecipe(recipe));
  const [publishAsCurated, setPublishAsCurated] = useState(recipe?.is_community === false);
  const [submitting, setSubmitting] = useState(false);

  const onMethodChange = (newMethod) => {
    setForm({ ...form, method: newMethod });
    // Only refill defaults if user hasn't entered any value yet
    const empty = !params.dose_g && !params.yield_g && !params.temp_c && !params.time;
    if (empty) setParams(paramsFromDefaults(newMethod));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        title_en: form.title_en,
        title_ar: form.title_ar,
        method: form.method,
        difficulty: form.difficulty,
        image_url: form.image_url || null,
        ingredients_en: splitList(form.ingredients_en),
        ingredients_ar: splitList(form.ingredients_ar),
        steps_en: splitList(form.steps_en),
        steps_ar: splitList(form.steps_ar),
        params: paramsToPayload(params),
      };
      // Admin can toggle community/curated when editing
      if (isAdmin) payload.is_community = !publishAsCurated;
      await endpoints.updateRecipe(recipe.id, payload);
      toast.success(isRTL ? "تم الحفظ" : "Saved");
      navigate("/recipes");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Could not save"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="edit-recipe-page">
      <button
        onClick={() => navigate("/recipes")}
        className="text-xs uppercase tracking-widest text-[#5C544F] hover:text-[#C86240] inline-flex items-center gap-1.5 mb-4"
        data-testid="edit-recipe-back"
      >
        <ArrowLeft size={12} />
        {t.common.back}
      </button>
      <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-4">
        {isRTL ? "تعديل" : "Edit"}
      </div>
      <h1 className={`${serifClass} text-4xl lg:text-5xl text-[#2A2421] leading-[1.05]`}>
        {lang === "ar" ? recipe.title_ar : recipe.title_en}
      </h1>

      <form onSubmit={submit} className="mt-12 space-y-6 soft-card rounded-2xl p-8" data-testid="edit-recipe-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t.addRecipe.titleEn}>
            <Input value={form.title_en} onChange={set("title_en")} required data-testid="edit-title-en" className="h-11 rounded-xl border-[#2A2421]/15" />
          </Field>
          <Field label={t.addRecipe.titleAr}>
            <Input value={form.title_ar} onChange={set("title_ar")} required dir="rtl" data-testid="edit-title-ar" className="h-11 rounded-xl border-[#2A2421]/15" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={t.addRecipe.method}>
            <Select value={form.method} onValueChange={onMethodChange}>
              <SelectTrigger className="h-11 rounded-xl border-[#2A2421]/15" data-testid="edit-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {methods.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{lang === "ar" ? m.name_ar : m.name_en}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t.addRecipe.difficulty}>
            <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
              <SelectTrigger className="h-11 rounded-xl border-[#2A2421]/15" data-testid="edit-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="easy">{t.difficulty.easy}</SelectItem>
                <SelectItem value="medium">{t.difficulty.medium}</SelectItem>
                <SelectItem value="hard">{t.difficulty.hard}</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={t.addRecipe.imageUrl}>
            <ImageUploader
              value={form.image_url}
              onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
              testid="edit-image"
              aiPromptSuggestion={`${form.title_en || form.title_ar || "specialty coffee"} — ${form.method || "pour-over"} brew, warm magazine light, ceramic cup, shallow depth of field`}
            />
          </Field>
        </div>

        <RecipeParamsForm method={form.method} params={params} onChange={setParams} testIdPrefix="edit-params" />

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t.addRecipe.ingredientsEn}>
            <Textarea rows={5} value={form.ingredients_en} onChange={set("ingredients_en")} required data-testid="edit-ingredients-en" className="rounded-xl border-[#2A2421]/15" />
          </Field>
          <Field label={t.addRecipe.ingredientsAr}>
            <Textarea rows={5} value={form.ingredients_ar} onChange={set("ingredients_ar")} required dir="rtl" data-testid="edit-ingredients-ar" className="rounded-xl border-[#2A2421]/15" />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={`${t.addRecipe.stepsEn} (${isRTL ? "اختياري" : "optional"})`}>
            <Textarea rows={6} value={form.steps_en} onChange={set("steps_en")} data-testid="edit-steps-en" className="rounded-xl border-[#2A2421]/15" />
          </Field>
          <Field label={`${t.addRecipe.stepsAr} (${isRTL ? "اختياري" : "optional"})`}>
            <Textarea rows={6} value={form.steps_ar} onChange={set("steps_ar")} dir="rtl" data-testid="edit-steps-ar" className="rounded-xl border-[#2A2421]/15" />
          </Field>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="save-recipe-btn"
          className="w-full h-12 rounded-xl bg-[#C86240] hover:bg-[#b15637] text-white"
        >
          {submitting ? t.common.loading : t.common.save}
        </Button>

        {isAdmin && (
          <div
            className="flex items-center justify-between gap-4 rounded-xl border border-[#C86240]/30 bg-[#C86240]/5 px-4 py-3"
            data-testid="edit-admin-curated-toggle-row"
          >
            <div className={isRTL ? "text-right" : "text-left"}>
              <div className="text-sm font-semibold text-[#2A2421]">
                {isRTL ? "نشر في القسم المنتقى" : "Publish in Curated section"}
              </div>
              <div className="text-xs text-[#5C544F] mt-0.5">
                {isRTL
                  ? "خاص بالمشرف — عند التفعيل تنتقل الوصفة إلى المنتقاة."
                  : "Admin only — moves recipe to the Curated list."}
              </div>
            </div>
            <Switch
              checked={publishAsCurated}
              onCheckedChange={setPublishAsCurated}
              data-testid="edit-publish-as-curated-switch"
            />
          </div>
        )}
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-[0.2em] text-[#5C544F]">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);
